import { PageHeader } from "@/components/ui/design-system";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { procitajGreske } from "@/lib/greske";
import {
  analizirajDatoteku,
  preuzmiIzvjestaj,
  preuzmiPredlozak,
  TARIFNE_GRUPE,
  uveziDatoteku,
  type IzvjestajUvoza,
  type OdlukaOMjestu,
} from "@/lib/radnici";

/**
 * Excel import — `docs/model-podataka-faza1-regulatorno.md` §9.
 *
 * Three steps on one screen, in the order they happen: take the template, see
 * what would happen, then let it happen. The middle step writes nothing, and
 * saying so on the screen is what makes the third one safe to press.
 *
 * The report is per row, with the row number from the spreadsheet — the number
 * the person is looking at while they fix it.
 */

type Korak = "odabir" | "analiza" | "gotovo";

export function UvozRadnikaPage() {
  const [datoteka, setDatoteka] = useState<File | null>(null);
  const [izvjestaj, setIzvjestaj] = useState<IzvjestajUvoza | null>(null);
  const [korak, setKorak] = useState<Korak>("odabir");
  const [radi, setRadi] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [odluke, setOdluke] = useState<Record<string, OdlukaOMjestu>>({});

  async function preuzmi(
    dohvat: () => Promise<Blob>,
    naziv: string,
  ): Promise<void> {
    const blob = await dohvat();
    const adresa = URL.createObjectURL(blob);
    const veza = document.createElement("a");
    veza.href = adresa;
    veza.download = naziv;
    veza.click();
    URL.revokeObjectURL(adresa);
  }

  async function analiziraj(odabrana: File): Promise<void> {
    setRadi(true);
    setGreska(null);
    try {
      const rezultat = await analizirajDatoteku(odabrana);
      setIzvjestaj(rezultat);
      setDatoteka(odabrana);
      setKorak("analiza");
    } catch (error) {
      const poruke = procitajGreske(error);
      setGreska(
        poruke.opcenito[0] ??
          Object.values(poruke.polja)[0] ??
          "Datoteku nije moguće pročitati.",
      );
    } finally {
      setRadi(false);
    }
  }

  async function uvezi(): Promise<void> {
    if (!datoteka) return;
    setRadi(true);
    setGreska(null);
    try {
      const rezultat = await uveziDatoteku(datoteka, Object.values(odluke));
      setIzvjestaj(rezultat);
      setKorak("gotovo");
    } catch (error) {
      const poruke = procitajGreske(error);
      setGreska(
        poruke.opcenito[0] ?? Object.values(poruke.polja)[0] ?? "Uvoz nije uspio.",
      );
    } finally {
      setRadi(false);
    }
  }

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <PageHeader title={<>Uvoz zaposlenika iz Excela</>} meta={<><p className="text-[var(--color-muted-foreground)]">
          Zdravstveni podaci i zaštita na radu ne uvoze se kroz Excel — za njih
          postoje ekrani u aplikaciji, uz evidenciju pristupa.
        </p></>} />

      {greska ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-[var(--color-status-bad-fg)]/25 bg-[var(--color-status-bad-bg)] p-4 text-[var(--color-status-bad-fg)]"
        >
          {greska}
        </div>
      ) : null}

      <section className="avm-surface flex flex-col gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <h2 className="text-[var(--text-card)] font-semibold">1. Predložak</h2>
        <p className="text-[var(--color-muted-foreground)]">
          Predložak ima točne nazive stupaca i jedan primjer retka. Obvezno je
          ime, prezime i OIB; ostalo se može dopuniti kasnije.
        </p>
        <div>
          <Button
            variant="outline"
            onClick={() =>
              void preuzmi(preuzmiPredlozak, "avm-predlozak-radnici.xlsx")
            }
          >
            Preuzmi predložak
          </Button>
        </div>
      </section>

      <section className="avm-surface flex flex-col gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <h2 className="text-[var(--text-card)] font-semibold">
          2. Provjera datoteke
        </h2>
        <p className="text-[var(--color-muted-foreground)]">
          Provjera samo čita datoteku. Ništa se ne upisuje dok ne potvrdite
          uvoz.
        </p>
        <label className="flex flex-col gap-2">
          <span className="oznaka">Excel datoteka</span>
          <input
            type="file"
            accept=".xlsx"
            aria-label="Excel datoteka"
            onChange={(dogadaj) => {
              const odabrana = dogadaj.target.files?.[0];
              if (odabrana) void analiziraj(odabrana);
            }}
            className="rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white p-2"
          />
        </label>
        {radi && korak === "odabir" ? <p>Provjeravam datoteku…</p> : null}
      </section>

      {izvjestaj ? (
        <section className="flex flex-col gap-4">
          <h2 className="text-[var(--text-section)] font-semibold">
            {korak === "gotovo" ? "3. Uvoz je gotov" : "3. Rezultat provjere"}
          </h2>

          <div className="flex flex-wrap gap-4">
            <Brojka
              naziv={korak === "gotovo" ? "Uvezeno" : "Za uvoz"}
              vrijednost={izvjestaj.za_uvoz}
              ton="ok"
            />
            <Brojka
              naziv="Preskočeno"
              vrijednost={izvjestaj.preskocenih}
              ton={izvjestaj.preskocenih > 0 ? "warn" : undefined}
            />
            <Brojka naziv="Redaka u datoteci" vrijednost={izvjestaj.redaka} />
          </div>

          {izvjestaj.upozorenja.length > 0 ? (
            <div className="rounded-[var(--radius-card)] border border-[var(--color-status-warn-fg)]/25 bg-[var(--color-status-warn-bg)] p-4">
              {izvjestaj.upozorenja.map((upozorenje) => (
                <p key={upozorenje} className="text-[var(--color-status-warn-fg)]">
                  {upozorenje}
                </p>
              ))}
            </div>
          ) : null}

          {korak === "analiza" && izvjestaj.nepoznata_radna_mjesta.length > 0 ? (
            <NepoznataMjesta
              nazivi={izvjestaj.nepoznata_radna_mjesta}
              odluke={odluke}
              onOdluka={(naziv, odluka) =>
                setOdluke((prethodne) => ({ ...prethodne, [naziv]: odluka }))
              }
            />
          ) : null}

          {korak === "analiza" && izvjestaj.nepoznata_drzavljanstva.length > 0 ? (
            <div className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
              <h3 className="text-[var(--text-card)] font-semibold">
                Nepoznata državljanstva
              </h3>
              <p className="mt-2 text-[var(--color-muted-foreground)]">
                {izvjestaj.nepoznata_drzavljanstva.join(", ")} — dodaju se u
                šifrarnik s najstrožim režimom (treće zemlje). Ako je koja od
                njih EU ili regija, ispravite je u šifrarniku nakon uvoza.
              </p>
            </div>
          ) : null}

          <Tablica stavke={izvjestaj.stavke} />

          <div className="flex flex-wrap gap-3">
            {korak === "analiza" ? (
              <Button onClick={() => void uvezi()} disabled={radi || izvjestaj.za_uvoz === 0}>
                {radi
                  ? "Uvozim…"
                  : `Uvezi ${izvjestaj.za_uvoz} ${redaka(izvjestaj.za_uvoz)}`}
              </Button>
            ) : (
              <Link to="/zaposlenici">
                <Button>Otvori popis zaposlenika</Button>
              </Link>
            )}
            {datoteka ? (
              <Button
                variant="outline"
                onClick={() =>
                  void preuzmi(
                    () => preuzmiIzvjestaj(datoteka),
                    "avm-izvjestaj-uvoza.xlsx",
                  )
                }
              >
                Preuzmi izvještaj
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function redaka(broj: number): string {
  return broj === 1 ? "redak" : "redaka";
}

function Brojka({
  naziv,
  vrijednost,
  ton,
}: {
  naziv: string;
  vrijednost: number;
  ton?: "ok" | "warn";
}) {
  return (
    <div className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] px-6 py-4">
      <p className="oznaka">{naziv}</p>
      <p
        className={
          ton === "ok"
            ? "broj text-[var(--text-section)] font-bold text-[var(--color-status-ok-fg)]"
            : ton === "warn"
              ? "broj text-[var(--text-section)] font-bold text-[var(--color-status-warn-fg)]"
              : "broj text-[var(--text-section)] font-bold"
        }
      >
        {vrijednost}
      </p>
    </div>
  );
}

function NepoznataMjesta({
  nazivi,
  odluke,
  onOdluka,
}: {
  nazivi: string[];
  odluke: Record<string, OdlukaOMjestu>;
  onOdluka: (naziv: string, odluka: OdlukaOMjestu) => void;
}) {
  return (
    <div className="avm-surface flex flex-col gap-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
      <div>
        <h3 className="text-[var(--text-card)] font-semibold">
          Nepoznata radna mjesta
        </h3>
        <p className="mt-1 text-[var(--color-muted-foreground)]">
          Odaberite tarifnu grupu da se radno mjesto doda u šifrarnik. Bez toga
          se radnik uvozi bez radnog mjesta i karton ostaje nepotpun.
        </p>
      </div>
      {nazivi.map((naziv) => (
        <label key={naziv} className="flex flex-wrap items-center gap-3">
          <span className="min-w-48 font-medium">{naziv}</span>
          <DesignSelect
            value={odluke[naziv]?.nova_tarifna_grupa ?? ""}
            onChange={(dogadaj) =>
              onOdluka(naziv, {
                naziv,
                nova_tarifna_grupa: dogadaj.target.value || undefined,
              })
            }
            aria-label={`Tarifna grupa za ${naziv}`}
            className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
          >
            <option value="">— ostavi prazno —</option>
            {TARIFNE_GRUPE.map((grupa) => (
              <option key={grupa} value={grupa}>
                Tarifna grupa {grupa}
              </option>
            ))}
          </DesignSelect>
        </label>
      ))}
    </div>
  );
}

function Tablica({ stavke }: { stavke: IzvjestajUvoza["stavke"] }) {
  return (
    <div className="avm-surface overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="oznaka px-4 py-3">Redak</th>
            <th className="oznaka px-4 py-3">Zaposlenik</th>
            <th className="oznaka px-4 py-3">OIB</th>
            <th className="oznaka px-4 py-3">Ishod</th>
            <th className="oznaka px-4 py-3">Razlog</th>
          </tr>
        </thead>
        <tbody>
          {stavke.map((stavka) => {
            const preskocen =
              stavka.ishod === "preskace_se" || stavka.ishod === "preskocen";
            return (
              <tr
                key={stavka.redak}
                className={
                  preskocen
                    ? "h-[var(--spacing-row)] border-b border-[var(--color-border)] bg-[var(--color-bad-row)] last:border-b-0"
                    : "h-[var(--spacing-row)] border-b border-[var(--color-border)] last:border-b-0"
                }
              >
                <td className="broj px-4">{stavka.redak}</td>
                <td className="px-4">
                  {[stavka.prezime, stavka.ime].filter(Boolean).join(" ") || "—"}
                </td>
                <td className="broj px-4">{stavka.oib || "—"}</td>
                <td className="px-4">
                  <Badge ton={preskocen ? "warn" : "ok"}>
                    {stavka.ishod === "uvezen"
                      ? "Uvezen"
                      : stavka.ishod === "uvozi_se"
                        ? "Uvozi se"
                        : "Preskočen"}
                  </Badge>
                </td>
                <td className="px-4 text-[var(--color-muted-foreground)]">
                  {stavka.razlog ?? "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
